import {
  CopyButton,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import { WIDGET_H3 } from "@/components/tool-kit/styles";
import {
  PLOT_PALETTE,
  VectorPlot,
} from "@/components/tools/linear-algebra/vector-plot";
import {
  angleText,
  br,
  decimalText,
  quad,
  type VectorCtx,
  val,
} from "@/components/tools/linear-algebra/vector-results";
import { t } from "@/lib/i18n/interpolate";
import type { ParsedAngle } from "@/lib/tool-kit/linear-algebra/angle-input";
import { trigOfDegrees } from "@/lib/tool-kit/linear-algebra/exact-angle";
import {
  mulQ,
  type QuadNumber,
  quadFromRational,
  quadToComplex,
} from "@/lib/tool-kit/linear-algebra/quad-number";
import {
  directionAngles,
  normSquared,
  toPolar,
  type Vec,
  magnitude as vecMagnitude,
} from "@/lib/tool-kit/linear-algebra/vector";
import {
  isZeroR,
  type Rational,
  toNumberR,
} from "@/lib/tool-kit/math/rational";

function approx(ctx: VectorCtx, x: QuadNumber): string {
  return isZeroR(x.b) ? "" : ` ≈ ${decimalText(ctx, x)}`;
}

/** Magnitude and direction of a 2D or 3D vector given by its components. */
export function ComponentsToDirection({
  vec,
  ctx,
}: {
  vec: Vec;
  ctx: VectorCtx;
}) {
  const { strings } = ctx;
  if (vec.length === 3) {
    return <Direction3D ctx={ctx} vec={vec} />;
  }
  const polar = toPolar(vec[0], vec[1]);
  if (polar === null) {
    return (
      <p className="text-muted-foreground text-sm">{strings.zeroVector}</p>
    );
  }
  const [x, y] = vec;
  const quadrantName =
    polar.quadrant === 0
      ? strings.onAxis
      : t(strings.quadrantN, { n: strings.quadrants[polar.quadrant] });
  const arctan =
    (Math.atan(Math.abs(toNumberR(y) / toNumberR(x))) * 180) / Math.PI;
  return (
    <>
      <StatGrid>
        <StatCard
          emphasis
          label={strings.magnitudeLabel}
          value={`${quad(ctx, polar.magnitude)}${approx(ctx, polar.magnitude)}`}
        />
        <StatCard
          label={strings.directionLabel}
          value={angleText(ctx, polar.degrees, polar.exactDegrees)}
        />
        <StatCard label={strings.quadrantLabel} value={quadrantName} />
      </StatGrid>
      <div className="space-y-1 rounded-lg border border-border bg-muted/30 p-4 font-mono text-muted-foreground text-sm">
        <p>
          {t(strings.magnitudeWorking, {
            x: br(ctx, x),
            y: br(ctx, y),
            sum: val(ctx, normSquared(vec)),
            magnitude: quad(ctx, polar.magnitude),
          })}
        </p>
        <p>
          {t(strings.directionWorking, {
            y: val(ctx, y),
            x: val(ctx, x),
            angle: angleText(ctx, polar.degrees, polar.exactDegrees),
          })}
        </p>
        {polar.quadrant === 0 ? null : (
          <p>
            {t(strings.referenceWorking, {
              ref: ctx.fmt(arctan, { maximumFractionDigits: 4 }),
              quadrant: strings.quadrants[polar.quadrant],
            })}
          </p>
        )}
      </div>
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copyResults}
        small
        text={`|v| = ${quad(ctx, polar.magnitude)}\nθ = ${angleText(ctx, polar.degrees, polar.exactDegrees)}`}
      />
      <h3 className={WIDGET_H3}>{strings.plotHeading}</h3>
      <VectorPlot
        arrows={[
          {
            x: toNumberR(x),
            y: toNumberR(y),
            color: PLOT_PALETTE[0],
            label: "v",
          },
        ]}
        downloadLabel={strings.downloadPng}
        fileName="vector.png"
        title={strings.plotTitleOne}
      />
    </>
  );
}

function Direction3D({ vec, ctx }: { vec: Vec; ctx: VectorCtx }) {
  const { strings } = ctx;
  const angles = directionAngles(vec);
  if (angles === null) {
    return (
      <p className="text-muted-foreground text-sm">{strings.zeroVector}</p>
    );
  }
  const axes = ["x", "y", "z"];
  const length = vecMagnitude(vec);
  return (
    <>
      <StatGrid>
        <StatCard
          emphasis
          label={strings.magnitudeLabel}
          value={`${quad(ctx, length)}${approx(ctx, length)}`}
        />
        {axes.map((axis, i) => (
          <StatCard
            key={axis}
            label={t(strings.angleWithAxis, { axis })}
            value={angleText(ctx, angles.degrees[i], angles.exactDegrees[i])}
          />
        ))}
      </StatGrid>
      <div className="space-y-1 rounded-lg border border-border bg-muted/30 p-4 font-mono text-muted-foreground text-sm">
        <p>
          {t(strings.cosinesWorking, {
            cosines: angles.cosines.map((c) => quad(ctx, c)).join(", "),
          })}
        </p>
        <p>{strings.cosinesCheck}</p>
      </div>
    </>
  );
}

/** Components from a magnitude and a direction angle (2D). */
export function PolarToComponents({
  r,
  angle,
  ctx,
}: {
  r: Rational;
  angle: ParsedAngle;
  ctx: VectorCtx;
}) {
  const { strings } = ctx;
  const trig =
    angle.exactDegrees === null ? null : trigOfDegrees(angle.exactDegrees);
  const radians = (angle.degrees * Math.PI) / 180;
  const rn = toNumberR(r);
  const exact: [QuadNumber, QuadNumber] | null = trig
    ? [mulQ(quadFromRational(r), trig.cos), mulQ(quadFromRational(r), trig.sin)]
    : null;
  const xValue = exact ? quadToComplex(exact[0]).re : rn * Math.cos(radians);
  const yValue = exact ? quadToComplex(exact[1]).re : rn * Math.sin(radians);
  const xText = exact
    ? `${quad(ctx, exact[0])}${approx(ctx, exact[0])}`
    : ctx.fmt(clean(xValue), { maximumFractionDigits: 6 });
  const yText = exact
    ? `${quad(ctx, exact[1])}${approx(ctx, exact[1])}`
    : ctx.fmt(clean(yValue), { maximumFractionDigits: 6 });
  return (
    <>
      <StatGrid>
        <StatCard emphasis label={strings.xComponent} value={xText} />
        <StatCard emphasis label={strings.yComponent} value={yText} />
      </StatGrid>
      <div className="space-y-1 rounded-lg border border-border bg-muted/30 p-4 font-mono text-muted-foreground text-sm">
        <p>
          {t(strings.componentWorking, {
            r: br(ctx, r),
            angle: angleText(ctx, angle.degrees, angle.exactDegrees),
          })}
        </p>
        <p>{t(strings.componentX, { x: xText })}</p>
        <p>{t(strings.componentY, { y: yText })}</p>
        {exact ? null : <p>{strings.decimalNote}</p>}
      </div>
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copyResults}
        small
        text={`(${xText}, ${yText})`}
      />
      <h3 className={WIDGET_H3}>{strings.plotHeading}</h3>
      <VectorPlot
        arrows={[{ x: xValue, y: yValue, color: PLOT_PALETTE[0], label: "v" }]}
        downloadLabel={strings.downloadPng}
        fileName="vector.png"
        title={strings.plotTitleOne}
      />
    </>
  );
}

function clean(value: number): number {
  return Math.abs(value) < 1e-12 ? 0 : value;
}
